import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import {
  listContent,
  reorderContent,
  setContentVisible,
  undeleteContent,
} from "../../../services/api/contentAdminService";
import { isAuthError, isCanceledError } from "../../../services/errors/errorHandler";
import { formatBusinessDateTime } from "../../../services/utils/dateUtils";
import AdminHeader from "../AdminHeader";
import VisibleSwitch from "./VisibleSwitch";
import { CONTENT_HOME_PATH, SETTINGS_ITEMS, collectionPath, getCollection, itemPath, itemTitle } from "./collections";
import "../admin.css";
import "./content.css";

/**
 * One collection: its items in page order, each with a Visible switch, Edit,
 * and Move up / Move down (buttons, so reordering works one-handed on a
 * phone); "Add new" (created hidden); and Deleted items with Undelete.
 *
 * `settings` has three fixed items: no add, delete, reorder or deleted view.
 */

const VIEWS = [
  { id: "current", label: "On the list" },
  { id: "deleted", label: "Deleted items" },
];

function ItemRow({ item, index, count, collection, fixed, busy, onToggle, onMove }) {
  const titleId = `cms-item-${item.id}-title`;
  return (
    <li className="admin-booking cms-item" data-item-id={item.id}>
      <div className="cms-item__head">
        <h2 className="admin-booking__time cms-item__title" id={titleId}>{itemTitle(item)}</h2>
        {fixed && SETTINGS_ITEMS[item.slug] && <p className="cms-hint">{SETTINGS_ITEMS[item.slug].blurb}</p>}
      </div>
      <div className="cms-item__controls">
        <VisibleSwitch
          id={`cms-item-${item.id}-visible`}
          labelledBy={titleId}
          checked={Boolean(item.is_visible)}
          busy={busy}
          onToggle={() => onToggle(item)}
        />
        <div className="admin-actions">
          <Link className="admin-btn admin-btn--primary cms-link-btn" to={itemPath(collection, item.id)} aria-describedby={titleId}>
            Edit
          </Link>
          {!fixed && (
            <>
              <button
                type="button"
                id={`cms-move-up-${item.id}`}
                className="admin-btn"
                disabled={busy || index === 0}
                onClick={() => onMove(index, -1)}
                aria-label={`Move “${itemTitle(item)}” up`}
              >
                ↑ Up
              </button>
              <button
                type="button"
                id={`cms-move-down-${item.id}`}
                className="admin-btn"
                disabled={busy || index === count - 1}
                onClick={() => onMove(index, 1)}
                aria-label={`Move “${itemTitle(item)}” down`}
              >
                ↓ Down
              </button>
            </>
          )}
        </div>
      </div>
    </li>
  );
}

export default function CollectionScreen() {
  const { collection } = useParams();
  const location = useLocation();
  const meta = getCollection(collection);
  const fixed = Boolean(meta && meta.fixed);

  const [view, setView] = useState("current");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState((location.state && location.state.notice) || "");
  const [actionError, setActionError] = useState("");
  const focusAfterMove = useRef(null);

  useEffect(() => {
    if (!meta) return undefined;
    const controller = new AbortController();
    setLoading(true);
    setError("");
    listContent(collection, { deleted: view === "deleted", signal: controller.signal })
      .then((data) => {
        if (controller.signal.aborted) return;
        setItems(data.items || []);
        setLoading(false);
      })
      .catch((err) => {
        if (controller.signal.aborted || isCanceledError(err)) return;
        if (!isAuthError(err)) setError(err.message || "Could not load this list.");
        setLoading(false);
      });
    return () => controller.abort();
  }, [collection, meta, view, reloadKey]);

  // Keep focus on the moved item's button, so repeated taps keep moving it.
  useEffect(() => {
    const target = focusAfterMove.current;
    if (!target || busy) return;
    focusAfterMove.current = null;
    const same = document.getElementById(`cms-move-${target.dir}-${target.id}`);
    const other = document.getElementById(`cms-move-${target.dir === "up" ? "down" : "up"}-${target.id}`);
    const el = same && !same.disabled ? same : other;
    if (el && !el.disabled) el.focus();
  });

  const clearMessages = () => {
    setNotice("");
    setActionError("");
  };

  const toggle = async (item) => {
    clearMessages();
    setBusy(true);
    try {
      const updated = await setContentVisible(collection, item.id, !item.is_visible);
      setItems((prev) => prev.map((it) => (it.id === item.id ? { ...it, ...updated } : it)));
      setNotice(updated.is_visible ? `“${itemTitle(item)}” is on the site now.` : `“${itemTitle(item)}” is hidden now.`);
    } catch (err) {
      if (!isAuthError(err)) setActionError(err.message || "That didn’t work. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const move = async (index, step) => {
    const target = index + step;
    if (target < 0 || target >= items.length) return;
    clearMessages();
    const before = items;
    const next = items.slice();
    [next[index], next[target]] = [next[target], next[index]];
    setItems(next);
    setBusy(true);
    focusAfterMove.current = { id: next[target].id, dir: step < 0 ? "up" : "down" };
    try {
      const data = await reorderContent(collection, next.map((it) => it.id));
      if (data && Array.isArray(data.items)) setItems(data.items);
      setNotice(`Moved “${itemTitle(next[target])}” to place ${target + 1} of ${next.length}.`);
    } catch (err) {
      setItems(before);
      if (!isAuthError(err)) setActionError(err.message || "Could not change the order. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const undelete = async (item) => {
    clearMessages();
    setBusy(true);
    try {
      await undeleteContent(collection, item.id);
      setItems((prev) => prev.filter((it) => it.id !== item.id));
      setNotice(`Brought back “${itemTitle(item)}”. It’s hidden until you switch it on, at the end of the list.`);
    } catch (err) {
      if (!isAuthError(err)) setActionError(err.message || "Could not bring it back. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  if (!meta) {
    return (
      <div className="admin">
        <AdminHeader />
        <main id="main" className="admin-main cms" tabIndex={-1}>
          <h1>Not found</h1>
          <p className="admin-muted">There’s no area of the site with that name.</p>
          <Link className="admin-btn cms-link-btn" to={CONTENT_HOME_PATH}>Back to Site content</Link>
        </main>
      </div>
    );
  }

  return (
    <div className="admin">
      <AdminHeader />
      <main id="main" className="admin-main cms" tabIndex={-1}>
        <Link className="cms-back" to={CONTENT_HOME_PATH}>‹ Site content</Link>
        <h1>{meta.label}</h1>
        <p className="admin-muted">{meta.blurb}</p>

        {!fixed && (
          <>
            <Link className="admin-btn admin-btn--primary cms-link-btn cms-add" to={`${collectionPath(collection)}/new`}>
              + Add a new {meta.noun}
            </Link>
            <p className="cms-hint">New ones start hidden, so you can check them before anyone sees them.</p>
            <div className="admin-filters" role="group" aria-label="Show">
              {VIEWS.map((v) => (
                <button
                  key={v.id}
                  type="button"
                  aria-pressed={view === v.id}
                  className={`admin-tab ${view === v.id ? "is-active" : ""}`}
                  onClick={() => {
                    clearMessages();
                    setView(v.id);
                  }}
                >
                  {v.label}
                </button>
              ))}
            </div>
          </>
        )}

        <div aria-live="polite" className="admin-live">
          {notice && <p className="admin-notice">{notice}</p>}
        </div>
        {actionError && <p className="admin-error" role="alert">{actionError}</p>}

        <section aria-label={view === "deleted" ? "Deleted items" : meta.label} aria-busy={loading}>
          {loading && <p className="admin-muted" role="status">Loading…</p>}
          {!loading && error && (
            <div className="admin-error" role="alert">
              <p>{error}</p>
              <button type="button" className="admin-btn" onClick={() => setReloadKey((k) => k + 1)}>Try again</button>
            </div>
          )}
          {!loading && !error && items.length === 0 && (
            <p className="admin-empty">
              {view === "deleted" ? "Nothing has been deleted." : `No ${meta.noun}s yet. Add one above.`}
            </p>
          )}
          {!loading && !error && items.length > 0 && view === "current" && (
            <ul className="admin-list">
              {items.map((item, index) => (
                <ItemRow
                  key={item.id}
                  item={item}
                  index={index}
                  count={items.length}
                  collection={collection}
                  fixed={fixed}
                  busy={busy}
                  onToggle={toggle}
                  onMove={move}
                />
              ))}
            </ul>
          )}
          {!loading && !error && items.length > 0 && view === "deleted" && (
            <ul className="admin-list">
              {items.map((item) => (
                <li key={item.id} className="admin-booking cms-item" data-item-id={item.id}>
                  <h2 className="admin-booking__time cms-item__title">{itemTitle(item)}</h2>
                  <p className="cms-hint">Deleted {formatBusinessDateTime(item.updated_at)}</p>
                  <button
                    type="button"
                    className="admin-btn"
                    disabled={busy}
                    onClick={() => undelete(item)}
                    aria-label={`Undelete “${itemTitle(item)}”`}
                  >
                    Undelete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>
    </div>
  );
}
